import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { storageService } from '@/services/supabase/storage';

interface UseImageUploadProps {
  user: any;
  onImageUrlChange: (urls: string[]) => void;
  bucketName: string;
}

export function useImageUpload({ user, onImageUrlChange, bucketName }: UseImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploadAttempts, setUploadAttempts] = useState(0);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [compressionStats, setCompressionStats] = useState<string | null>(null);
  const { toast } = useToast();
  
  const maxUploadAttempts = 3;
  
  const resetUpload = useCallback(() => {
    setCurrentError(null);
    setUploadAttempts(0);
    setImagePreviews([]);
  }, []);
  
  // Create a data URL from a file for local preview
  const createLocalPreview = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to create data URL'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }, []);

  const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setCurrentError(null);
    setUploadAttempts(0);

    try {
      // Create previews for all files
      const previewPromises = Array.from(files).map(file => createLocalPreview(file));
      const previews = await Promise.all(previewPromises);
      setImagePreviews(previews);

      // Upload files
      const uploadPromises = Array.from(files).map(async (file) => {
        let attempts = 0;
        while (attempts < maxUploadAttempts) {
          try {
            const url = await storageService.uploadCaseStudyImage(file, user.id);
            return url;
          } catch (error) {
            attempts++;
            setUploadAttempts(attempts);
            if (attempts === maxUploadAttempts) {
              throw error;
            }
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
          }
        }
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      onImageUrlChange(uploadedUrls);
      
      toast({
        title: "Images uploaded",
        description: `${uploadedUrls.length} image${uploadedUrls.length > 1 ? 's' : ''} uploaded successfully.`,
      });
    } catch (error: any) {
      setCurrentError(error.message || 'Failed to upload images');
      toast({
        title: "Upload failed",
        description: error.message || 'Failed to upload images',
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setImagePreviews([]);
    }
  }, [user, onImageUrlChange, createLocalPreview, toast]);

  return {
    uploading,
    imagePreviews,
    currentError,
    handleImageUpload,
    uploadAttempts,
    maxUploadAttempts,
    resetUpload,
    compressionStats
  };
} 